import { useCallback, useEffect, useRef, useState } from "react";
import ShieldWallWidget from "./shieldwall/components/ShieldWallWidget";
import type {
  ShieldWallError,
  ShieldWallOutcome,
} from "./shieldwall/components/ShieldWallWidget";
import {
  ACTION,
  API_BASE,
  CONFIG_ERROR,
  SITE_KEY,
  TOKEN_TTL_MS,
  fetchRuntimeConfig,
  type RuntimeConfig,
} from "./lib/config";
import { byId, type StateId } from "./lib/states";
import { reportFalsePositive, submitPost } from "./lib/wall";
import { useWallFeed } from "./lib/useWallFeed";
import Masthead from "./components/Masthead";
import Wall from "./components/Wall";
import ComposeForm, { type Note } from "./components/ComposeForm";
import PrivacyDetail from "./components/PrivacyDetail";
import Footer from "./components/Footer";
import ReportDialog from "./components/ReportDialog";
import StateInspector from "./components/StateInspector";

const COOLDOWN_FALLBACK_MS = 60_000;

export default function App() {
  const { posts, reactions, mine, addLocal, toggleReaction } = useWallFeed();
  const [state, setState] = useState<StateId>("sdk-loading");
  const [outcome, setOutcome] = useState<ShieldWallOutcome | null>(null);
  const [landedId, setLandedId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [note, setNote] = useState<Note | null>(null);

  /** Bumping this remounts the widget, which is how a re-check is requested. */
  const [widgetKey, setWidgetKey] = useState(0);

  /** responseToken + when it was issued, for the Worker's siteverify redeem. */
  const token = useRef<{ value: string; at: number } | null>(null);
  /** Distinguishes state 2 (passive pass) from state 5 (passed a challenge). */
  const sawChallenge = useRef(false);
  /** A submit that is waiting on a silent token re-issue (state 8's happy path). */
  const pendingSubmit = useRef<{ name: string; message: string } | null>(null);
  /** The inspector freezes live callbacks so a design review is not clobbered. */
  const frozen = useRef(false);

  const inspecting = new URLSearchParams(location.search).has("states");

  /** null until /api/config resolves; the widget waits so it mounts once. */
  const [runtime, setRuntime] = useState<RuntimeConfig | null>(null);

  useEffect(() => {
    fetchRuntimeConfig()
      .then(setRuntime)
      // No /api/config (local dev without the Functions host, or the API is
      // down) — fall back to the build-time key rather than block the form.
      .catch(() => setRuntime({ siteKey: SITE_KEY, warning: CONFIG_ERROR }));
  }, []);

  /* ── widget callbacks ─────────────────────────────────────────────── */

  const handleResult = useCallback((result: ShieldWallOutcome) => {
    if (frozen.current) return;
    setOutcome(result);
    if (result.responseToken) token.current = { value: result.responseToken, at: Date.now() };
    if (result.verdict === "CHALLENGE_REQUIRED") {
      sawChallenge.current = true;
      setState("challenge");
    }
  }, []);

  const handleChallengeRequired = useCallback(() => {
    if (frozen.current) return;
    sawChallenge.current = true;
    setState("challenge");
  }, []);

  const handleVerified = useCallback(() => {
    if (frozen.current) return;
    setState(sawChallenge.current ? "passed" : "passive");
  }, []);

  const handleError = useCallback((err: ShieldWallError) => {
    if (frozen.current) return;
    token.current = null;
    if (err.kind === "rate-limited") {
      setCooldownUntil(Date.now() + COOLDOWN_FALLBACK_MS);
      setState("limited");
    } else if (err.kind === "rejected") {
      setState("failed");
    } else {
      // network or unsupported — degraded path, never a hard fail
      setState("offline");
    }
    // A submit waiting on a silent re-issue has now definitively failed.
    if (pendingSubmit.current) {
      pendingSubmit.current = null;
      setState(err.kind === "rejected" ? "failed" : "expired");
    }
  }, []);

  /* ── re-arm ───────────────────────────────────────────────────────── */

  const rearm = useCallback(() => {
    token.current = null;
    sawChallenge.current = false;
    setOutcome(null);
    setState("sdk-loading");
    setWidgetKey((k) => k + 1);
  }, []);

  /**
   * Release the hold when the cooldown runs out.
   *
   * The countdown in the form is display only — without this the state machine
   * sat in `limited` after it reached 0:00 and the only way out was a refresh.
   * Verification survives the wait when the token is still inside its TTL;
   * otherwise re-arm so the visitor is not handed a token the Worker will
   * reject the moment they press post.
   */
  useEffect(() => {
    if (state !== "limited") return;

    const release = () => {
      const held = token.current;
      const fresh = held && Date.now() - held.at < TOKEN_TTL_MS;
      if (fresh) setState(sawChallenge.current ? "passed" : "passive");
      else rearm();
    };

    // Small grace so the client never releases a moment before the Worker's own
    // window closes, which would spend a token on a guaranteed 429.
    const wait = Math.max(0, cooldownUntil - Date.now()) + 500;
    const t = window.setTimeout(release, wait);
    return () => window.clearTimeout(t);
  }, [state, cooldownUntil, rearm]);

  /* ── submit ───────────────────────────────────────────────────────── */

  const post = useCallback(
    async (name: string, message: string) => {
      if (Date.now() < cooldownUntil) {
        setState("limited");
        return;
      }

      const degraded = state === "offline";
      const held = token.current;
      const stale = !held || Date.now() - held.at > TOKEN_TTL_MS;

      // Token aged out while the page sat open. Re-issue silently and let the
      // widget's next verdict resume this submit — the user sees nothing.
      if (!degraded && stale) {
        pendingSubmit.current = { name, message };
        rearm();
        return;
      }

      setState("submitting");
      const result = await submitPost({ name, message, token: held?.value ?? null });

      if (result.ok) {
        setCooldownUntil(Date.now() + COOLDOWN_FALLBACK_MS);

        if (result.pending) {
          // Queued, not posted. Saying "it is on the wall" here would be a lie
          // the visitor can check in one scroll.
          setNote({
            tag: "Queued",
            text: "Verification could not run, so a person reads this one first. It goes up after that.",
            tone: "ok",
          });
          setState("offline");
          return;
        }

        addLocal(result.post);
        setLandedId(result.post.id);
        setState("submitted");
        // Held in App state, not in the state table: re-arming the widget below
        // moves the slot back to passive within a second, and a note that lived
        // on the state spec would disappear with it before it had been read.
        setNote({ tag: "Posted", text: "It is at the top of the wall.", tone: "ok" });
        // The token is single-use — get a fresh one for the next post.
        token.current = null;
        sawChallenge.current = false;
        setWidgetKey((k) => k + 1);
        return;
      }

      setNote(null);

      switch (result.kind) {
        case "rate":
          setCooldownUntil(Date.now() + (result.retryAfterMs ?? COOLDOWN_FALLBACK_MS));
          setState("limited");
          break;
        case "bot":
          setState("failed");
          break;
        case "expired":
          setState("expired");
          break;
        default:
          setState("rejected");
      }
    },
    [cooldownUntil, state, rearm],
  );

  // Resume a submit that was parked for a silent token re-issue.
  useEffect(() => {
    if (!pendingSubmit.current) return;
    if (state !== "passive" && state !== "passed") return;
    const parked = pendingSubmit.current;
    pendingSubmit.current = null;
    void post(parked.name, parked.message);
  }, [state, post]);

  /* ── slot actions ─────────────────────────────────────────────────── */

  const onAct = useCallback(
    (act: string) => {
      if (act === "report") return setReportOpen(true);
      rearm();
    },
    [rearm],
  );

  const onReportSend = useCallback(
    (note: string) => {
      setReportOpen(false);
      void reportFalsePositive({
        note,
        reasonCodes: outcome?.reasonCodes ?? [],
        score: outcome?.score ?? null,
      });
    },
    [outcome],
  );

  /* ── inspector ────────────────────────────────────────────────────── */

  const onInspect = useCallback((id: StateId) => {
    frozen.current = true;
    if (id === "limited") setCooldownUntil(Date.now() + COOLDOWN_FALLBACK_MS);
    setState(id);
  }, []);

  const spec = byId(state);

  // Held back until the runtime config lands, so the widget mounts exactly once
  // and does not run a first challenge unattributed and then a second with the
  // key. Until then the slot sits in state 1, which is what it is for.
  // siteKey is optional on the widget — without one the live API still issues
  // anonymous challenges (no console telemetry). Missing key is a soft warning.
  const widget = runtime ? (
    <ShieldWallWidget
      key={widgetKey}
      apiBase={API_BASE}
      siteKey={runtime.siteKey || undefined}
      action={ACTION}
      onVerified={handleVerified}
      onResult={handleResult}
      onChallengeRequired={handleChallengeRequired}
      onError={handleError}
    />
  ) : null;

  return (
    <>
      <Masthead />
      <main className="shell">
        <div className="board">
          <div className="board__feed">
            <Wall
              posts={posts}
              landedId={landedId}
              reactions={reactions}
              mine={mine}
              onReact={(id, emoji) => void toggleReaction(id, emoji)}
            />
          </div>
          <div className="board__compose">
            <ComposeForm
              spec={spec}
              widget={widget}
              configError={runtime ? runtime.warning : null}
              cooldownUntil={cooldownUntil}
              noteOverride={note}
              onSubmit={post}
              onAct={onAct}
            />
          </div>
        </div>
        <PrivacyDetail />
      </main>
      <Footer />
      <ReportDialog
        open={reportOpen}
        outcome={outcome}
        onSend={onReportSend}
        onCancel={() => setReportOpen(false)}
      />
      {inspecting && <StateInspector current={state} onPick={onInspect} />}
    </>
  );
}
